import formatDate from 'date-fns/format';

import createKm from '@ott/utility-create-km';

import { FIELDS } from '../constants';
import getSuggestionsByCategories from '../helpers/getSuggestionsByCategories';
import { getSearchHistory } from '../helpers/saveSearchHistory';
import {
  checkIfSuggestionIsGeoAutocompleteOption,
  GeoAutocompleteOption,
  SearchForm,
  StationType,
  Suggestion,
  TargetType,
} from '../types';
import { commonKmDataGetters } from './kissmetrics';

const checkIfHasSearchHistory = () => Boolean(getSearchHistory().length);

const km = createKm('busSearchForm', commonKmDataGetters);

type Direction = typeof FIELDS.DEPARTURE | typeof FIELDS.DESTINATION;

const getSuggestionTypesData = (suggestions: Suggestion[]) => {
  let geoPointCount = 0;
  let stationCount = 0;

  const stationsCountByType: Record<StationType, number> = {
    [StationType.Airport]: 0,
    [StationType.RailwayStation]: 0,
    [StationType.BusTerminal]: 0,
    [StationType.BusStation]: 0,
    [StationType.MetroStation]: 0,
    [StationType.Mall]: 0,
  };

  suggestions.forEach((suggestion) => {
    if (checkIfSuggestionIsGeoAutocompleteOption(suggestion)) {
      if (suggestion.targetType === TargetType.GeoPoint) {
        geoPointCount++;
      } else if (suggestion.entityType) {
        stationCount++;
        stationsCountByType[suggestion.entityType]++;
      }
    }
  });

  return {
    geoPointCount,
    stationCount,
    stationsCountByType,
  };
};

export const trackSuggestionsOpen = (params: {
  direction: Direction;
  suggestions: Suggestion[];
  input?: SearchForm['departure'] | SearchForm['destination'];
}) => {
  const { direction, suggestions, input } = params;

  const suggestionsByCategories = getSuggestionsByCategories(suggestions);
  const { history, popular, nearest, russian, otherCountries } = suggestionsByCategories;

  km('bus_suggestions_open', {
    direction,
    suggestionsCountByCategories: {
      history: history.length,
      popular: popular.length,
      nearest: nearest.length,
      russian: russian.length,
      otherCountries: otherCountries.length,
    },
    hasSearchHistory: checkIfHasSearchHistory(),
    input,
    ...getSuggestionTypesData(suggestions),
  });
};

export const trackHistoryLabelsShow = (params: { labelsCount: number }) => {
  km('bus_history_labels_show', params);
};

export const trackSuggestionSelect = (params: {
  suggestions: Suggestion[];
  timestampWhenWasOpened: number | null;
  direction: typeof FIELDS.DEPARTURE | typeof FIELDS.DESTINATION;
  input: string;
  option: GeoAutocompleteOption | null;
}) => {
  const { direction, option, input, suggestions, timestampWhenWasOpened } = params;

  const geoSuggestionsOnly = suggestions.filter(
    (suggestion) => !suggestion.isOptionGroup
  ) as GeoAutocompleteOption[];
  const selectionOrder =
    geoSuggestionsOnly.findIndex((suggestion) => suggestion.value === option?.value) + 1;

  const selectionTimeStamp = Date.now();
  const timeToSelect = timestampWhenWasOpened
    ? (selectionTimeStamp - timestampWhenWasOpened) / 1000
    : null;

  const suggestionsByCategories = getSuggestionsByCategories(suggestions);

  const selectionGroup = Object.entries(suggestionsByCategories).find(([groupName, items]) => {
    return items.some((item) => item.value === option?.value);
  });
  const selectionType = selectionGroup ? selectionGroup[0] : null;

  // По просьбе Полины оставляю все параметры с прежними наименованиями
  km('bus_suggestion_select', {
    ts: formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss.SSSSSS'),
    destination: direction,
    geo_id: option?.value,
    input,
    selectionType,
    selectionOrder,
    timeToSelect,
    cnt: geoSuggestionsOnly.length,
    cnt_history: suggestionsByCategories.history.length,
    cnt_top_geo: suggestionsByCategories.nearest.length,
    cnt_top_orders: suggestionsByCategories.popular.length,
    cnt_top_russian: suggestionsByCategories.russian.length,
    cnt_top_other_countries: suggestionsByCategories.otherCountries.length,
    hasSearchHistory: checkIfHasSearchHistory(),
    suggestionType: option?.targetType,
    stationType: option?.entityType,
    ...getSuggestionTypesData(suggestions),
  });
};

export const trackEmptySuggestion = (params: { input: string; direction: Direction }) => {
  km('bus_suggestion_empty', {
    ...params,
    hasSearchHistory: checkIfHasSearchHistory(),
  });
};

export const trackSubmitFailed = (params: {
  fieldsWithError: string[];
  departureInput: string;
  destinationInput: string;
}) => {
  km('bus_search_submit_failed', params);
};

export const trackSwapClick = (params: { isChanged: boolean }) => {
  km('bus_search_switch', params);
};
